import React, { ReactElement } from 'react';
import Moment from 'react-moment';

import { TTableColumn, TUseTableHooks } from '../../components/blocks/FnDataTable/types';
import { BASE_DATE_FORMAT } from '../../constants/date';
import { TTMockupBOMZip, TMockupKSF } from './redux/types';
import FnColorCircle from '../../components/elements/FnColorCircle';
import KsfButton from '../../components/elements/FnKsfButton';
import { headerStyle, upperHeaderStyle } from '../../components/blocks/FnDataTable/styles';
import FnImageWithReviewNative from '../../components/elements/FnImageWithReviewNative';
import { SpanDescription, DivWrapper } from '../../components/elements/FnImageDescription/styles';
import FnButtonDownload from '../../components/elements/FnButtonDownload';
import { getCurrencySymbol, inchToCm } from '../../util/common';

export const INITIAL_REQUEST = {
  accountId: '',
  filterItems: [],
  page: 1,
  pageLimit: 10,
  requestPage: '',
  sortItems: []
};

export const TABLE_COLUMNS_KSF: TTableColumn<TMockupKSF>[] = [
  {
    Header: '',
    accessor: 'product',
    style: {
      width: '60px'
    },
    Cell: ({ row }: TUseTableHooks<TMockupKSF>): ReactElement => {
      const { original } = row;
      return <FnImageWithReviewNative image={original.product?.image} />;
    }
  },
  {
    Header: 'Directory',
    accessor: 'skuDirectory',
    style: {
      width: '90px'
    },
    Cell: ({ row }: TUseTableHooks<TMockupKSF>): ReactElement => {
      const { original } = row;
      return <div>{original.skuDirectory}</div>;
    }
  },
  {
    Header: 'Panel',
    accessor: 'printPanel',
    style: {
      width: '90px'
    },
    Cell: ({ row }: TUseTableHooks<TMockupKSF>): ReactElement => {
      const { original } = row;
      return <div>{original.printPanel}</div>;
    }
  },
  {
    Header: 'Blank color',
    accessor: 'colorGroup',
    style: {
      width: '150px'
    },
    Cell: ({ row }: TUseTableHooks<TMockupKSF>): ReactElement => {
      const { original } = row;
      return <FnColorCircle color={original.colorGroup} />;
    }
  },
  {
    Header: 'Size Range',
    accessor: 'sizeRange',
    style: {
      width: '90px'
    },
    Cell: ({ row }: TUseTableHooks<TMockupKSF>): ReactElement => {
      const { original } = row;
      return <div>{original.sizeRange}</div>;
    }
  },
  {
    Header: 'Time stamp',
    accessor: 'createdDate',
    style: {
      width: '150px'
    },
    Cell: ({ row }: TUseTableHooks<TMockupKSF>): ReactElement => {
      return <Moment format={BASE_DATE_FORMAT} date={row.original.createdDate} />;
    }
  },
  {
    Header: 'Creation',
    accessor: 'creationType',
    style: {
      width: '90px'
    },
    Cell: ({ row }: TUseTableHooks<TMockupKSF>): ReactElement => {
      const { original } = row;
      return <div>{original.creationType}</div>;
    }
  },
  {
    Header: 'MIM',
    accessor: 'mediaName',
    style: {
      width: '150px'
    },
    Cell: ({ row }: TUseTableHooks<TMockupKSF>): ReactElement => {
      const { original } = row;
      return <div>{original.mediaName}</div>;
    }
  },
  {
    Header: 'Directory',
    accessor: 'directoryItems',
    style: {
      width: '90px'
    },
    sticky: 'right',
    Cell: ({ row }: TUseTableHooks<TMockupKSF>): ReactElement => {
      const { original } = row;
      const { directoryItems } = original;
      const fileItems = directoryItems && directoryItems.length ? directoryItems[0]?.directoryFiles : [];
      const directoryName = directoryItems[0]?.printer?.directoryName;
      return <KsfButton data={fileItems} directoryName={directoryName} />;
    }
  }
];

export const TABLE_COLUMNS_BOM: TTableColumn<TTMockupBOMZip>[] = [
  {
    Header: 'Identifiers',
    style: {
      width: '480px',
      ...upperHeaderStyle
    },
    columns: [
      {
        Header: 'SKU Prefix',
        accessor: 'articleNumber',
        style: {
          width: '240px'
        }
      },
      {
        Header: 'SKU (Customer item code)',
        accessor: 'sku',
        style: {
          width: '240px'
        }
      }
    ]
  },
  {
    Header: 'Blank',
    style: {
      width: '740px',
      ...upperHeaderStyle
    },
    columns: [
      {
        Header: 'Blank Model',
        style: {
          width: '400px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          return (
            <DivWrapper>
              <FnImageWithReviewNative image={original.blankUri} />
              <SpanDescription>{original.blankModel}</SpanDescription>
            </DivWrapper>
          );
        }
      },
      {
        Header: 'Color',
        accessor: 'color',
        style: {
          width: '150px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          return <FnColorCircle color={original.color} />;
        }
      },
      {
        Header: 'Size',
        accessor: 'size',
        style: {
          width: '150px',
          ...headerStyle
        }
      }
    ]
  },
  {
    Header: 'Panel',
    style: {
      width: '160px',
      ...upperHeaderStyle
    },
    columns: [
      {
        Header: 'Print Panel',
        accessor: 'printPanel',
        style: {
          width: '160px'
        }
      }
    ]
  },
  {
    Header: 'Asset (Artwork / Printed trim)',
    style: {
      width: '2070px',
      ...upperHeaderStyle
    },
    columns: [
      {
        Header: 'Print Area',
        accessor: 'printArea',
        style: {
          width: '120px',
          ...headerStyle
        }
      },
      {
        Header: 'Asset ID',
        style: {
          width: '120px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const standartId = original.artwork?.standartId || '';
          return <div>{standartId}</div>;
        }
      },
      {
        Header: 'Asset Name',
        style: {
          width: '120px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const name = original.artwork?.name || '';
          return <div>{name}</div>;
        }
      },
      {
        Header: 'Asset Image',
        style: {
          width: '120px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          return (
            <DivWrapper>
              <FnImageWithReviewNative image={original.artwork?.image} />
              <SpanDescription>{original.artwork?.fileName || ''}</SpanDescription>
            </DivWrapper>
          );
        }
      },
      {
        Header: 'Asset Print Price',
        style: {
          width: '90px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const assetPrice = original.artwork?.assetPrice || '';
          return <div>{assetPrice}</div>;
        }
      },
      {
        Header: 'Asset Printing Method',
        style: {
          width: '120px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const printingMethod = original.artwork?.printingMethod || '';
          return <div>{printingMethod}</div>;
        }
      },
      {
        Header: 'Canvas Size (cm)',
        style: {
          width: '120px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const width =
            original.artwork?.canvasSizeWidth !== undefined && original.artwork?.canvasSizeWidth !== null
              ? inchToCm(original.artwork?.canvasSizeWidth)
              : null;
          const height =
            original.artwork?.canvasSizeHeight !== undefined && original.artwork?.canvasSizeHeight !== null
              ? inchToCm(original.artwork?.canvasSizeHeight)
              : null;

          if (width !== null && height !== null) {
            return (
              <div>
                {width} X {height}
              </div>
            );
          }
          return null;
        }
      },
      {
        Header: 'Canvas Location (cm)',
        style: {
          width: '120px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const canvasLocation =
            original.artwork?.canvasLocation !== undefined && original.artwork?.canvasLocation !== null
              ? inchToCm(original.artwork?.canvasSizeWidth)
              : null;

          if (canvasLocation !== null) {
            return <div>Starts {canvasLocation} below the neck</div>;
          }
          return null;
        }
      },
      {
        Header: 'Alignment',
        style: {
          width: '120px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const alignment = original.artwork?.alignment || '';
          return <div>{alignment}</div>;
        }
      },
      {
        Header: 'Print Pallet',
        style: {
          width: '120px',
          ...headerStyle
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const printPallete = original.artwork?.printPallete || '';
          return <div>{printPallete}</div>;
        }
      }
    ]
  },
  {
    Header: 'Product',
    style: {
      width: '1740px',
      ...upperHeaderStyle
    },
    columns: [
      {
        Header: 'Product Name',
        style: {
          width: '240px'
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const productName = original.product?.name || '';
          return <div>{productName}</div>;
        }
      },
      {
        Header: 'System Mockup',
        style: {
          width: '400px'
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          return (
            <DivWrapper>
              <FnImageWithReviewNative image={original.product?.image} />
              <SpanDescription>{original.product?.fileName || ''}</SpanDescription>
            </DivWrapper>
          );
        }
      },
      {
        Header: 'External Mockup',
        style: {
          width: '400px'
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          return (
            <DivWrapper>
              <FnImageWithReviewNative image={original.externalMockupFile?.uri} />
              <SpanDescription>{original.externalMockupFile?.storageName || ''}</SpanDescription>
            </DivWrapper>
          );
        }
      },
      {
        Header: 'Retail Price',
        style: {
          width: '400px'
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const currency = getCurrencySymbol(original.product?.retailPrice?.currencyCode);
          const value =
            typeof original.product?.retailPrice?.currencyValue === 'number'
              ? original.product?.retailPrice?.currencyValue
              : null;

          if (value !== null) {
            return (
              <div>
                {currency} {value}
              </div>
            );
          }
          return null;
        }
      },
      {
        Header: 'Wholesale Price',
        style: {
          width: '400px'
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const currency = getCurrencySymbol(original.product?.wholesalePrice?.currencyCode);
          const value =
            typeof original.product?.wholesalePrice?.currencyValue === 'number'
              ? original.product?.wholesalePrice?.currencyValue
              : null;

          if (value !== null) {
            return (
              <div>
                {currency} {value}
              </div>
            );
          }
          return null;
        }
      },
      {
        Header: 'Additional Files', // TODO: add component
        style: {
          width: '400px'
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          return null;
        }
      }
    ]
  },
  {
    Header: 'Production',
    style: {
      width: '580px',
      ...upperHeaderStyle
    },
    columns: [
      {
        Header: 'Reference Number',
        style: {
          width: '140px',
          ...headerStyle
        },
        accessor: 'referenceNumber'
      },
      {
        Header: 'Reference Sticker',
        style: {
          width: '140px',
          ...headerStyle
        },
        accessor: 'referenceSticker'
      },
      {
        Header: 'Packaging',
        style: {
          width: '140px',
          ...headerStyle
        },
        accessor: 'packaging'
      },
      {
        Header: 'Download Files',
        style: {
          width: '130px'
        },
        Cell: ({ row }: TUseTableHooks<TTMockupBOMZip>): ReactElement => {
          const { original } = row;
          const { url, correlationId } = original.zipUri;
          const skuCatalogItemId = original.skuCatalogItemId;
          return skuCatalogItemId ? <FnButtonDownload uri={`${url}${skuCatalogItemId}/${correlationId}`} /> : null;
        }
      }
    ]
  }
];
